import { Fragment, useEffect, useMemo, useState } from 'react';
import {
  P2GAccordion,
  P2GBox,
  P2GButton,
  P2GCheckBox,
  P2GChip,
  P2GDivider,
  P2GDropdown,
  P2GGrid,
  P2GIcon,
  P2GLink,
  P2GProgress,
  P2GText,
  P2GTextBox,
  SvgCheckCircle,
  SvgNumber1,
  SvgNumber2,
  SvgStarFull
} from '@parcel2go/core-ui';
import {
  Country,
  Customer,
  InputRuleApiModel,
  MarketplaceOrderApiModel,
  PackageApiModel,
  ProductApiModel,
  SmartSendItemApiModel
} from '~/lib/api/smartsend/types';
import { CreateMeProductRequest, CreateMeProductResponse } from '~/lib/api/me/types';
import { ApiResponse } from '~/lib/api/api-response';
import { isDefault } from '../../my-products/product-helpers';
import { IsDefaultAddress } from '~/apps/smartsend/lib/helpers/IsDefaultAddress';
import { AppCard } from '~/components/card/app-card';
import AlertWithIcon from '../../AlertWithIcon';
import { ConfigSetting, useTenant } from '@parcel2go/npm-p2g-app';
import Loading from '~/components/loading/Loading';
import { SmartSendCreateFormValidation } from '~/apps/smartsend/lib/helpers/SmartSendValidation';
import { AddressLookup } from '../../address/op/types';
import AddressTypeahead from '../../address/typeahead';
import useMapAddressLookup from '../../address/MapAddressLookupToCustomer';
import { CustomerSelector } from '../../customer/CustomerSelector';
import { ProductSelector } from '../../product/ProductSelector';
import { PanelButtons } from '../panels/PanelButtons';
import { DimensionsSelection } from '../DimensionsSelection';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { uid } from '~/apps/smartsend/lib/helpers/uid';
import { TruncateString } from '~/apps/smartsend/lib/helpers/StringHelper';
import SidePanelSection, { SidePanelSections } from '~/components/sidepanel-section';
import { ContentsSelectionRow } from '../ContentsSelectionRow';
import GetTariffSystemFromItem from '~/apps/smartsend/lib/helpers/GetTariffSystemFromItem';
import {
  ActiveSection,
  AppliedProductSnapshot,
  PACKAGE_REQUIRED_VALIDATION_CODES,
  RECIPIENT_REQUIRED_VALIDATION_CODES,
  applyProductToLine,
  buildInitialItem,
  buildProductPayloadFromLine,
  createBlankOrderLine,
  lineCannotSaveAsProduct,
  lineIsIncomplete,
  matchingProductId,
  resolveInitialProduct,
  snapshotFromLine,
  stripCustomsFieldsIfDomestic,
  withRecalculatedTotals,
  withoutIndex
} from './NewShipmentFormHelpers';

interface NewShipmentFormProps {
  products: ProductApiModel[];
  initialProduct?: ProductApiModel | null;
  getCustomers: () => Promise<Customer[]>;
  getCountries: () => Promise<Country[]>;
  getPackages: () => Promise<PackageApiModel[]>;
  sender: Customer;
  onSave: (item: SmartSendItemApiModel) => void;
  onClose: () => void;
  createProduct: (product: CreateMeProductRequest) => Promise<ApiResponse<CreateMeProductResponse>>;
}

interface ShipmentItemLineProps {
  index: number;
  line: MarketplaceOrderApiModel;
  item: SmartSendItemApiModel;
  canRemove: boolean;
  isOpen: boolean;
  products: ProductApiModel[];
  quickSelectProducts: ProductApiModel[];
  appliedSnapshot: AppliedProductSnapshot | undefined;
  saveAsProduct: boolean;
  saveError: string | null;
  countries: Country[];
  productValidation: InputRuleApiModel[];
  onApplyProduct: (product: ProductApiModel) => void;
  onUpdate: (updated: MarketplaceOrderApiModel) => void;
  onOpen: () => void;
  onToggleSaveAsProduct: (checked: boolean) => void;
  onRemove: () => void;
}

const ShipmentItemLine = ({
  index,
  line,
  item,
  canRemove,
  isOpen,
  products,
  quickSelectProducts,
  appliedSnapshot,
  saveAsProduct,
  saveError,
  countries,
  productValidation,
  onApplyProduct,
  onUpdate,
  onOpen,
  onToggleSaveAsProduct,
  onRemove
}: ShipmentItemLineProps) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const appliedProductId = matchingProductId(appliedSnapshot, line);
  const isDefaultProduct = appliedProductId != null && isDefault(Number(appliedProductId));

  return (
    <SidePanelSection
      title={
        <P2GBox sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {t('ITEM_N_SUBTITLE', { defaultValue: 'Item {{n}}', n: index + 1 })}
          {isDefaultProduct && (
            <P2GChip size="small" color="default" label={t('DEFAULT_PRODUCT_TOOLTIP', { defaultValue: 'Default' })} />
          )}
        </P2GBox>
      }
      titleAdornment={
        canRemove && <P2GLink onClick={onRemove}>{t('REMOVE_ITEM_LINK', { defaultValue: 'Remove' })}</P2GLink>
      }
    >
      <P2GBox sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {isOpen && products.length > 0 && (
          <P2GBox sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <P2GText>{t('SAVED_PRODUCTS_LABEL', { defaultValue: 'Saved products' })}</P2GText>
            <P2GBox sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {quickSelectProducts.map((product) => {
                const isSelected = matchingProductId(appliedSnapshot, line) === product.Id;
                return (
                  <P2GChip
                    key={product.Id}
                    label={TruncateString(product.Description ?? '-', 20)}
                    icon={
                      isDefault(Number(product.Id)) ? (
                        <P2GIcon size="xs" palette={isSelected ? 'info' : 'primary'} children={<SvgStarFull />} />
                      ) : undefined
                    }
                    size="small"
                    color={isSelected ? 'primary' : 'info'}
                    onClick={() => onApplyProduct(product)}
                  />
                );
              })}
            </P2GBox>
            <P2GBox>
              <ProductSelector products={() => Promise.resolve(products)} onSelect={onApplyProduct}>
                <P2GLink>{t('BROWSE_ALL_PRODUCTS_LINK', { defaultValue: 'Browse Products' })}</P2GLink>
              </ProductSelector>
            </P2GBox>
          </P2GBox>
        )}

        <ContentsSelectionRow
          item={item}
          marketplaceOrder={line}
          productValidation={productValidation}
          countries={countries}
          tariffSystem={GetTariffSystemFromItem(item)}
          currencyCode={item.CurrencyCode}
          showReferenceNumber={false}
          onUpdate={onUpdate}
          onOpen={onOpen}
          startMinimised={Boolean(line.Product?.Description)}
        />

        {isOpen && !lineCannotSaveAsProduct(line) && !matchingProductId(appliedSnapshot, line) && (
          <P2GBox sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <P2GCheckBox
              id={`save-as-product-${index}`}
              error={false}
              labelPlacement="end"
              inputProps={{ checked: !!saveAsProduct }}
              changeHandler={(e) => {
                onToggleSaveAsProduct(e.target.checked);
                return true;
              }}
            >
              {t('SAVE_AS_PRODUCT_LABEL', { defaultValue: 'Save as product for faster shipping next time' })}
            </P2GCheckBox>
            {saveError && (
              <P2GText variant="caption" color="error">
                {saveError}
              </P2GText>
            )}
          </P2GBox>
        )}
      </P2GBox>
    </SidePanelSection>
  );
};

const NewShipmentForm = ({
  products,
  initialProduct,
  getCustomers,
  getCountries,
  getPackages,
  sender,
  onSave,
  onClose,
  createProduct
}: NewShipmentFormProps) => {
  const { settings } = useTenant();
  const { mapAddressLookupToCustomer } = useMapAddressLookup();
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const defaultOriginCountry = settings[ConfigSetting.DEFAULT_ORIGIN_COUNTRY];

  const [item, setItem] = useState<SmartSendItemApiModel>(() =>
    buildInitialItem(defaultOriginCountry, sender, initialProduct, products)
  );
  const [countries, setCountries] = useState<Country[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customersLoaded, setCustomersLoaded] = useState(false);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [activeSection, setActiveSection] = useState<ActiveSection>('recipient');
  const [recipientMode, setRecipientMode] = useState<'chips' | 'new'>('chips');
  const [dimensionsResetKey, setDimensionsResetKey] = useState(() => uid());
  const [formTouched, setFormTouched] = useState(false);
  const [appliedProductByIndex, setAppliedProductByIndex] = useState<Record<number, AppliedProductSnapshot>>(() => {
    const product = resolveInitialProduct(initialProduct, products);
    const firstLine = item.MarketplaceOrderLines[0];
    return product && firstLine ? { 0: snapshotFromLine(product.Id, firstLine) } : {};
  });
  const [saveAsProductByIndex, setSaveAsProductByIndex] = useState<Record<number, boolean>>({});
  const [saveErrorByIndex, setSaveErrorByIndex] = useState<Record<number, string | null>>({});
  const [openLineIndices, setOpenLineIndices] = useState<Record<number, boolean>>(() => {
    const firstLine = item.MarketplaceOrderLines[0];
    return firstLine && !firstLine.Product?.Description ? { 0: true } : {};
  });
  const [saving, setSaving] = useState(false);
  const [showSearch, setShowSearch] = useState(true);

  const hideDimensionsIfNotRequired = SettingsCache.settings.Features.includes('HideDimensionsIfNotRequired');

  useEffect(() => {
    getCountries().then(setCountries);
  }, [getCountries]);

  const selectedCountry = useMemo(
    () =>
      countries.find(
        (country) =>
          country.CountryIsoCode === item.Recipient.CountryIsoCode &&
          country.CountrySubdivision === item.Recipient.CountrySubdivision
      ),
    [countries, item.Recipient.CountryIsoCode, item.Recipient.CountrySubdivision]
  );

  const changeCountry = (iso3Code: string, subdivision: string, updatedItem: SmartSendItemApiModel) => {
    const matchedCountry = countries.find(
      (country) =>
        country.CountryIsoCode === iso3Code &&
        country.CountrySubdivision === (subdivision === 'null' ? null : subdivision)
    );
    if (matchedCountry) {
      updatedItem.Recipient.CountryIsoCode = matchedCountry.CountryIsoCode;
      updatedItem.Recipient.CountrySubdivision = matchedCountry.CountrySubdivision;
      if (
        matchedCountry.States?.length > 0 &&
        !matchedCountry.States.find((state) => state.Code === updatedItem.Recipient.County)
      ) {
        updatedItem.Recipient.County = matchedCountry.States[0].Code;
      }
    }
  };

  const changeBasedOnPostcode = (customer: Customer, updatedItem: SmartSendItemApiModel) => {
    const postcodeSwitch = selectedCountry?.Switches?.find((switchRule) =>
      new RegExp(switchRule.Pattern).test(customer?.Postcode?.toUpperCase())
    );
    if (postcodeSwitch) changeCountry(postcodeSwitch.ToIsoCode, postcodeSwitch.ToSubdivision, updatedItem);
  };

  const setRecipientField = (field: string, value: string) => {
    setFormTouched(true);
    const updatedItem = { ...item };
    (updatedItem.Recipient as any)[field] = value === 'null' ? null : value;
    if (field === 'Postcode') changeBasedOnPostcode(updatedItem.Recipient, updatedItem);
    setItem(updatedItem);
  };

  const selectSavedCustomer = (customer: Customer) => {
    setFormTouched(true);
    const updatedItem = { ...item, Recipient: { ...item.Recipient, ...customer } };
    changeBasedOnPostcode(customer, updatedItem);
    setItem(updatedItem);
    setRecipientMode('new');
  };

  const openRecipientSection = async () => {
    setActiveSection('recipient');
    if (!customersLoaded) {
      setCustomersLoading(true);
      const customerList = await getCustomers();
      setCustomers(customerList);
      setCustomersLoaded(true);
      setCustomersLoading(false);
    }
  };

  useEffect(() => {
    if (activeSection === 'recipient') {
      openRecipientSection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fall back to a saved address if sender is still the blank context placeholder.
  useEffect(() => {
    if (item.Sender?.Name || customers.length === 0) return;
    const fallbackSender = customers.find(IsDefaultAddress) || customers[0];
    setItem((current) => ({ ...current, Sender: { ...current.Sender, ...fallbackSender } }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers]);

  const handleAccordionChange = (name: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    if (!isExpanded) {
      setActiveSection(null);
      return;
    }
    if (name === 'recipient') {
      openRecipientSection();
    } else {
      setActiveSection(name as ActiveSection);
    }
  };

  const selectRecipientAddress = (switchToManualEntry: boolean) => async (selectedAddress: AddressLookup) => {
    setFormTouched(true);
    const mappedRecipient = await mapAddressLookupToCustomer(
      item.Recipient,
      selectedAddress,
      'Recipient',
      item.Recipient.AccountName || 'Default'
    );
    const updatedItem = { ...item, Recipient: mappedRecipient };
    changeBasedOnPostcode(mappedRecipient, updatedItem);
    setItem(updatedItem);
    if (switchToManualEntry) setRecipientMode('new');
    setShowSearch(false);
  };

  const renderAddressSearch = (onSelect: (address: AddressLookup) => void) =>
    showSearch ? (
      <AddressTypeahead required={false} onSelect={onSelect} countryIsoCode={item.Recipient.CountryIsoCode} search="" />
    ) : (
      <P2GLink onClick={() => setShowSearch(true)}>
        {t('SEARCH_FOR_ADDRESS_LINK', { defaultValue: 'Search for another address' })}
      </P2GLink>
    );

  const applyProduct = (product: ProductApiModel, index: number) => {
    setFormTouched(true);

    const updatedLine = applyProductToLine(item.MarketplaceOrderLines[index], product, defaultOriginCountry);

    setItem((current) => {
      const lines = current.MarketplaceOrderLines.map((line, i) => (i === index ? updatedLine : line));
      const updated = withRecalculatedTotals(current, lines);
      if (index === 0 && product.Dimensions) {
        updated.PackageDimensions = { ...updated.PackageDimensions, ...product.Dimensions };
      }
      return updated;
    });

    setAppliedProductByIndex((current) => ({ ...current, [index]: snapshotFromLine(product.Id, updatedLine) }));
    setSaveAsProductByIndex((current) => ({ ...current, [index]: false }));
    setSaveErrorByIndex((current) => ({ ...current, [index]: null }));
    if (index === 0 && product.Dimensions) {
      setDimensionsResetKey(uid());
    }
  };

  const updateLine = (index: number, updatedLine: MarketplaceOrderApiModel) => {
    setFormTouched(true);
    setItem((current) =>
      withRecalculatedTotals(
        current,
        current.MarketplaceOrderLines.map((line, i) => (i === index ? updatedLine : line))
      )
    );
  };

  const addItemRow = () => {
    setFormTouched(true);
    const newIndex = item.MarketplaceOrderLines.length;
    setItem((current) =>
      withRecalculatedTotals(current, [...current.MarketplaceOrderLines, createBlankOrderLine(defaultOriginCountry)])
    );
    setOpenLineIndices((current) => ({ ...current, [newIndex]: true }));
  };

  const removeItemRow = (index: number) => {
    if (item.MarketplaceOrderLines.length <= 1) return;

    setFormTouched(true);
    setItem((current) =>
      withRecalculatedTotals(
        current,
        current.MarketplaceOrderLines.filter((_, i) => i !== index)
      )
    );
    setAppliedProductByIndex((current) => withoutIndex(current, index));
    setSaveAsProductByIndex((current) => withoutIndex(current, index));
    setSaveErrorByIndex((current) => withoutIndex(current, index));
    setOpenLineIndices((current) => withoutIndex(current, index));
  };

  const isInternational = Boolean(
    item.Sender?.CountryIsoCode &&
    item.Recipient?.CountryIsoCode &&
    item.Sender.CountryIsoCode !== item.Recipient.CountryIsoCode
  );

  const handleSave = () => {
    if (saving) return;
    setSaving(true);

    const linesToSave = item.MarketplaceOrderLines.map((line, index) => ({ line, index })).filter(
      ({ line, index }) =>
        saveAsProductByIndex[index] &&
        !lineCannotSaveAsProduct(line) &&
        !matchingProductId(appliedProductByIndex[index], line)
    );

    linesToSave.forEach(({ line, index }) => {
      createProduct(buildProductPayloadFromLine(line, item.PackageDimensions)).then((result) => {
        if (!result.success) {
          setSaveErrorByIndex((current) => ({
            ...current,
            [index]: result.error?.message || 'Something went wrong saving this product. Please try again.'
          }));
        }
      });
    });

    onSave(withRecalculatedTotals(item, stripCustomsFieldsIfDomestic(item.MarketplaceOrderLines, isInternational)));
  };

  const validationErrors = useMemo(() => SmartSendCreateFormValidation(item), [item]);
  const formIsInvalid = validationErrors.length > 0 || item.MarketplaceOrderLines.some(lineIsIncomplete);
  const hasError = (code: string) => formTouched && validationErrors.includes(code);
  const packageIsComplete = !PACKAGE_REQUIRED_VALIDATION_CODES.some((code) => validationErrors.includes(code));
  const parcelSectionComplete = packageIsComplete && !item.MarketplaceOrderLines.some(lineIsIncomplete);
  const recipientComplete = !RECIPIENT_REQUIRED_VALIDATION_CODES.some((code) => validationErrors.includes(code));

  const senderAddressLine = [item.Sender.AddressLine1, item.Sender.AddressLine2].filter(Boolean).join(' ');
  const senderFullAddress = [senderAddressLine, item.Sender.Postcode].filter(Boolean).join(', ');

  const quickSelectProducts = useMemo(
    () => [...products].sort((a, b) => Number(isDefault(Number(b.Id))) - Number(isDefault(Number(a.Id)))).slice(0, 4),
    [products]
  );

  const productValidation: InputRuleApiModel[] = isInternational
    ? [
        { name: 'TariffCode', label: 'Tariff Code', value: '', required: false },
        { name: 'CountryOfManufacture', label: 'Country of Manufacture', value: '', required: true }
      ]
    : [];

  if (saving) {
    return (
      <AlertWithIcon icon={<P2GProgress />}>
        <P2GText>{t('CREATING_NEW_ITEM_ALERT', { defaultValue: 'Creating new item...' })}</P2GText>
      </AlertWithIcon>
    );
  }

  return (
    <>
      <SidePanelSections>
        <P2GText component="p" variant="body2">
          Enter shipment details
        </P2GText>
        <AppCard>
          <P2GBox p={2}>
            <P2GBox sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <P2GText variant="subtitle2">{t('SENDER_SUBTITLE', { defaultValue: 'Sender:' })}</P2GText>
              <P2GText>{item.Sender.Name}</P2GText>
            </P2GBox>
            <P2GBox>
              <P2GText>{senderFullAddress}</P2GText>
            </P2GBox>
          </P2GBox>
        </AppCard>

        <SidePanelSection>
          <P2GAccordion
            id="recipient-section"
            value="recipient"
            title="1. Where is it going?"
            summary={
              <P2GBox sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <P2GIcon
                  size="xs"
                  palette={recipientComplete ? 'success' : undefined}
                  children={recipientComplete ? <SvgCheckCircle /> : <SvgNumber1 />}
                />
                <P2GText variant="subtitle1">
                  <strong>Where is it going?</strong>
                </P2GText>
              </P2GBox>
            }
            onChange={() => handleAccordionChange('recipient')}
            expanded={activeSection === 'recipient'}
            sx={(theme) => ({
              marginBottom: theme.spacing(1),
              ...(recipientComplete && { border: '1px solid', borderColor: 'success.main' })
            })}
          >
            <P2GBox sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {countries.length > 0 && (
                <P2GDropdown
                  fullWidth
                  id="recipient-country"
                  required
                  label={t('SEND_TO_LABEL', { defaultValue: 'Sending to' })}
                  value={`${item.Recipient.CountryIsoCode}|${item.Recipient.CountrySubdivision}`}
                  options={countries.map((country) => ({
                    value: `${country.CountryIsoCode}|${country.CountrySubdivision}`,
                    text: country.LocalisedName,
                    key: `${country.CountryIsoCode}|${country.CountrySubdivision}|${country.Name}`
                  }))}
                  onChange={(v: string) => {
                    const updatedItem = { ...item };
                    changeCountry(v.split('|')[0], v.split('|')[1], updatedItem);
                    setItem(updatedItem);
                  }}
                />
              )}
              {customersLoading ? (
                <Loading />
              ) : customers.length > 0 && recipientMode === 'chips' ? (
                <>
                  {renderAddressSearch(selectRecipientAddress(true))}
                  <P2GBox sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                    {customers.slice(0, 3).map((customer) => (
                      <P2GChip
                        key={customer.Id ?? customer.Name}
                        label={`${customer.Name ?? ''} ${customer.Postcode ?? ''}`.trim()}
                        size="small"
                        variant="outlined"
                        color="info"
                        onClick={() => selectSavedCustomer(customer)}
                      />
                    ))}
                  </P2GBox>
                  <P2GBox>
                    <CustomerSelector
                      customers={getCustomers}
                      onSelect={selectSavedCustomer}
                      text={t('BROWSE_ADDRESSES_LINK', { defaultValue: 'Browse Saved Addresses' })}
                    />
                    {' | '}
                    <P2GLink onClick={() => setRecipientMode('new')}>
                      {t('MANUAL_ENTRY_LINK', { defaultValue: 'Manual Entry' })}
                    </P2GLink>
                  </P2GBox>
                </>
              ) : (
                <>
                  {renderAddressSearch(selectRecipientAddress(false))}
                  <P2GGrid container spacing={2}>
                    <P2GGrid item xs={6}>
                      <P2GTextBox
                        id="recipient-name"
                        label={t('NAME_LABEL', { defaultValue: 'Name' })}
                        value={item.Recipient.Name ?? ''}
                        required
                        maxLength={50}
                        fullWidth
                        showErrorsWhenPristine
                        error={hasError('RECIPIENT-NAME')}
                        customError="Name is required"
                        onChange={(value) => setRecipientField('Name', value)}
                      />
                    </P2GGrid>
                    <P2GGrid item xs={6}>
                      <P2GTextBox
                        id="recipient-organisation"
                        fullWidth
                        label={t('ORGANISATION_LABEL', { defaultValue: 'Organisation' })}
                        value={(item.Recipient as any).Organisation ?? ''}
                        maxLength={50}
                        onChange={(value) => setRecipientField('Organisation', value)}
                      />
                    </P2GGrid>
                    <P2GGrid item xs={6}>
                      <P2GTextBox
                        id="recipient-addressline1"
                        label={t('HOUSE_NUMBER_LABEL', { defaultValue: 'House Number' })}
                        value={item.Recipient.AddressLine1 ?? ''}
                        required
                        maxLength={50}
                        fullWidth
                        onChange={(value) => setRecipientField('AddressLine1', value)}
                      />
                    </P2GGrid>
                    <P2GGrid item xs={6}>
                      <P2GTextBox
                        id="recipient-addressline2"
                        label={t('STREET_LABEL', { defaultValue: 'Street' })}
                        value={item.Recipient.AddressLine2 ?? ''}
                        required
                        maxLength={50}
                        fullWidth
                        onChange={(value) => setRecipientField('AddressLine2', value)}
                      />
                    </P2GGrid>
                    <P2GGrid item xs={6}>
                      <P2GTextBox
                        id="recipient-town"
                        label={t('TOWN_LABEL', { defaultValue: 'Town' })}
                        value={item.Recipient.Town ?? ''}
                        required
                        maxLength={50}
                        fullWidth
                        showErrorsWhenPristine
                        error={hasError('RECIPIENT-TOWN')}
                        customError="Town is required"
                        onChange={(value) => setRecipientField('Town', value)}
                      />
                    </P2GGrid>
                    {selectedCountry && (
                      <P2GGrid
                        item
                        xs={6}
                        key={`county_${selectedCountry.CountryIsoCode}_${selectedCountry.States?.length}`}
                      >
                        {selectedCountry.States?.length > 0 ? (
                          <P2GDropdown
                            fullWidth
                            id="recipient-county"
                            label={t('COUNTY_LABEL', { defaultValue: 'County' })}
                            value={item.Recipient.County ?? ''}
                            options={selectedCountry.States.map((state) => ({
                              value: state.Code,
                              text: state.Name,
                              key: `${state.Code}_${state.Name}`
                            }))}
                            onChange={(v: string) => setRecipientField('County', v)}
                          />
                        ) : (
                          <P2GTextBox
                            id="recipient-county"
                            label={t('COUNTY_LABEL', { defaultValue: 'County' })}
                            value={item.Recipient.County ?? ''}
                            maxLength={50}
                            fullWidth
                            onChange={(value) => setRecipientField('County', value)}
                          />
                        )}
                      </P2GGrid>
                    )}
                    <P2GGrid item xs={6}>
                      <P2GTextBox
                        id="recipient-postcode"
                        label={t('POSTCODE_LABEL', { defaultValue: 'Postcode' })}
                        value={item.Recipient.Postcode ?? ''}
                        required
                        maxLength={50}
                        fullWidth
                        onChange={(value) => setRecipientField('Postcode', value)}
                      />
                    </P2GGrid>
                    <P2GGrid item xs={6}>
                      <P2GTextBox
                        id="recipient-phone"
                        label={t('PHONE_LABEL', { defaultValue: 'Phone' })}
                        fullWidth
                        type="tel"
                        maxLength={50}
                        value={item.Recipient.Phone ?? ''}
                        onChange={(value) => setRecipientField('Phone', value)}
                      />
                    </P2GGrid>
                    <P2GGrid item xs={12}>
                      <P2GTextBox
                        id="recipient-email"
                        label={t('EMAIL_LABEL', { defaultValue: 'Email' })}
                        fullWidth
                        type="email"
                        maxLength={256}
                        value={item.Recipient.Email ?? ''}
                        onChange={(value) => setRecipientField('Email', value)}
                      />
                    </P2GGrid>
                  </P2GGrid>
                  {customers.length > 0 && (
                    <CustomerSelector
                      customers={getCustomers}
                      onSelect={selectSavedCustomer}
                      text={t('BROWSE_ADDRESSES_LINK', { defaultValue: 'Browse Saved Addresses' })}
                    />
                  )}
                </>
              )}

              <P2GBox sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <P2GLink onClick={() => setActiveSection('parcel')}>
                  {t('CONTINUE_BUTTON', { defaultValue: 'Continue' })}
                </P2GLink>
              </P2GBox>
            </P2GBox>
          </P2GAccordion>

          <P2GAccordion
            id="parcel-section"
            value="parcel"
            title="2. What are you sending?"
            summary={
              <P2GBox sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <P2GIcon
                  size="xs"
                  palette={parcelSectionComplete ? 'success' : undefined}
                  children={parcelSectionComplete ? <SvgCheckCircle /> : <SvgNumber2 />}
                />
                <P2GText variant="subtitle1">
                  <strong>What are you sending?</strong>
                </P2GText>
              </P2GBox>
            }
            onChange={() => handleAccordionChange('parcel')}
            expanded={activeSection === 'parcel'}
            sx={(theme) => ({
              marginBottom: theme.spacing(1),
              ...(parcelSectionComplete && { border: '1px solid', borderColor: 'success.main' })
            })}
          >
            <SidePanelSections>
              <SidePanelSection title={t('PARCEL_SIZE_SUBTITLE', { defaultValue: 'Parcel Size' })}>
                <P2GBox sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <DimensionsSelection
                    key={dimensionsResetKey}
                    startMinimised={false}
                    dimensions={item.PackageDimensions}
                    onUpdate={(dimensions) => {
                      setFormTouched(true);
                      setItem((current) => ({ ...current, PackageDimensions: dimensions }));
                      setAppliedProductByIndex((current) => {
                        if (!(0 in current)) return current;
                        const { [0]: _removed, ...rest } = current;
                        return rest;
                      });
                    }}
                    dimensionsRequired={!hideDimensionsIfNotRequired}
                    hideDimensionsIfNotRequired={hideDimensionsIfNotRequired}
                    getPackages={getPackages}
                  />
                  {hasError('WEIGHT') && (
                    <P2GText variant="caption" color="error">
                      Weight is required
                    </P2GText>
                  )}
                  {hasError('DIMS') && (
                    <P2GText variant="caption" color="error">
                      Length, width and height are required
                    </P2GText>
                  )}
                </P2GBox>
              </SidePanelSection>

              <AppCard>
                <AppCard>
                  <P2GBox p={2} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {item.MarketplaceOrderLines.map((line, index) => (
                      <Fragment key={index}>
                        {index > 0 && <P2GDivider />}
                        <ShipmentItemLine
                          index={index}
                          line={line}
                          item={item}
                          canRemove={item.MarketplaceOrderLines.length > 1}
                          isOpen={!!openLineIndices[index]}
                          products={products}
                          quickSelectProducts={quickSelectProducts}
                          appliedSnapshot={appliedProductByIndex[index]}
                          saveAsProduct={!!saveAsProductByIndex[index]}
                          saveError={saveErrorByIndex[index] ?? null}
                          countries={countries}
                          productValidation={productValidation}
                          onApplyProduct={(product) => applyProduct(product, index)}
                          onUpdate={(updated) => updateLine(index, updated)}
                          onOpen={() => setOpenLineIndices((current) => ({ ...current, [index]: true }))}
                          onToggleSaveAsProduct={(checked) =>
                            setSaveAsProductByIndex((current) => ({ ...current, [index]: checked }))
                          }
                          onRemove={() => removeItemRow(index)}
                        />
                      </Fragment>
                    ))}
                  </P2GBox>
                </AppCard>
                <P2GBox p={2}>
                  <P2GButton fullWidth variant="outlined" onClick={addItemRow}>
                    {t('ADD_ITEM_LINK', { defaultValue: 'Add item' })}
                  </P2GButton>
                </P2GBox>
              </AppCard>

              <SidePanelSection title={t('ORDER_DETAILS_SUBTITLE', { defaultValue: 'Order Details:' })}>
                <P2GTextBox
                  id="reference-number"
                  fullWidth
                  label={t('REFERENCE_NUMBER_LABEL', { defaultValue: 'Reference Number' })}
                  value={item.MarketplaceOrderLines[0].MarketplaceOrderId ?? ''}
                  onChange={(value) => {
                    setFormTouched(true);
                    const updatedItem = { ...item };
                    updatedItem.MarketplaceOrderLines[0].MarketplaceOrderId = value;
                    setItem(updatedItem);
                  }}
                />
              </SidePanelSection>

              <P2GBox sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <P2GLink onClick={() => setActiveSection(null)}>
                  {t('CONTINUE_BUTTON', { defaultValue: 'Continue' })}
                </P2GLink>
              </P2GBox>
            </SidePanelSections>
          </P2GAccordion>
        </SidePanelSection>
      </SidePanelSections>

      <P2GBox mt={2} />
      <PanelButtons onSave={handleSave} saveDisabled={formIsInvalid || saving} onCancel={onClose} cancelText="Back" />
    </>
  );
};

export default NewShipmentForm;
